import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { WhatsAppButtonWithTemplates, WhatsAppIcon } from '@/components/WhatsAppButton';
import { openWhatsApp, WhatsAppTemplates } from '@/utils/whatsapp';
import { 
  Plus, 
  Search,
  FileText,
  Euro,
  User,
  Calendar,
  Trash2,
  Eye,
  Download,
  Factory,
  Send,
  Mail,
  Loader2,
  History,
  CheckCircle2,
  XCircle,
  Clock,
  UserCheck,
  AlertCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { pt } from 'date-fns/locale';

const STATUS_CONFIG = {
  rascunho: { label: 'Rascunho', color: 'bg-slate-100 text-slate-800' },
  pendente: { label: 'Pendente', color: 'bg-amber-100 text-amber-800' },
  confirmada: { label: 'Confirmada', color: 'bg-blue-100 text-blue-800' },
  enviada: { label: 'Enviada', color: 'bg-purple-100 text-purple-800' },
  concluida: { label: 'Concluída', color: 'bg-emerald-100 text-emerald-800' },
  cancelada: { label: 'Cancelada', color: 'bg-red-100 text-red-800' }
};

export default function OrdersPage() {
  const { api, user } = useAuth();
  const [orders, setOrders] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [viewingOrder, setViewingOrder] = useState(null);
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailHistory, setEmailHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [showHistory, setShowHistory] = useState(false);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [pendingStatusChange, setPendingStatusChange] = useState(null);
  const [notifyClient, setNotifyClient] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);
  
  const [form, setForm] = useState({
    client_id: '',
    client_name: '',
    items: [],
    subtotal: 0,
    discount: 0,
    total: 0,
    status: 'rascunho',
    notes: '',
    delivery_date: ''
  });

  const [newItem, setNewItem] = useState({
    product_id: '',
    product_name: '',
    quantity: 1,
    unit_price: 0
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [ordersRes, contactsRes, productsRes] = await Promise.all([
        api().get('/orders'),
        api().get('/contacts'),
        api().get('/products')
      ]);
      setOrders(ordersRes.data);
      setContacts(contactsRes.data);
      setProducts(productsRes.data.filter(p => p.active));
    } catch (error) {
      toast.error('Erro ao carregar dados');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (form.items.length === 0) {
      toast.error('Adicione pelo menos um produto à encomenda');
      return;
    }
    
    try {
      await api().post('/orders', form);
      toast.success('Nota de encomenda criada! Email enviado para a fábrica.');
      setShowModal(false);
      resetForm();
      loadData();
    } catch (error) {
      toast.error('Erro ao criar encomenda');
    }
  };

  const handleResendEmail = async (orderId) => {
    setSendingEmail(true);
    try {
      const res = await api().post(`/orders/${orderId}/send-email`);
      toast.success(res.data.message || 'Email enviado com sucesso!');
      // Reload history after sending
      loadEmailHistory(orderId);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao enviar email');
    } finally {
      setSendingEmail(false);
    }
  };

  const loadEmailHistory = async (orderId) => {
    setLoadingHistory(true);
    try {
      const res = await api().get(`/orders/${orderId}/email-history`);
      setEmailHistory(res.data);
    } catch (error) {
      console.error('Error loading email history:', error);
      setEmailHistory([]);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleViewOrder = (order) => {
    setViewingOrder(order);
    setShowViewModal(true);
    setShowHistory(false);
    loadEmailHistory(order.id);
  };

  const addItem = () => {
    if (!newItem.product_id || newItem.quantity <= 0) {
      toast.error('Selecione um produto e quantidade válida');
      return;
    }
    
    const total = newItem.quantity * newItem.unit_price;
    const item = {
      ...newItem,
      total
    };
    
    const updatedItems = [...form.items, item];
    const subtotal = updatedItems.reduce((sum, i) => sum + i.total, 0);
    const finalTotal = subtotal - form.discount;
    
    setForm({
      ...form,
      items: updatedItems,
      subtotal,
      total: finalTotal
    });
    
    setNewItem({
      product_id: '',
      product_name: '',
      quantity: 1,
      unit_price: 0
    });
  };

  const removeItem = (index) => {
    const updatedItems = form.items.filter((_, i) => i !== index);
    const subtotal = updatedItems.reduce((sum, i) => sum + i.total, 0);
    const finalTotal = subtotal - form.discount;
    
    setForm({
      ...form,
      items: updatedItems,
      subtotal,
      total: finalTotal
    });
  };

  const handleProductSelect = (productId) => {
    const product = products.find(p => p.id === productId);
    if (product) {
      setNewItem({
        ...newItem,
        product_id: product.id,
        product_name: product.name,
        unit_price: product.price
      });
    }
  };

  const handleClientSelect = (clientId) => {
    const client = contacts.find(c => c.id === clientId);
    if (client) {
      setForm({
        ...form,
        client_id: client.id,
        client_name: client.name
      });
    }
  };

  const handleStatusChange = async (orderId, newStatus) => {
    // For confirmada/enviada/concluida, show confirmation dialog to ask about client notification
    if (newStatus === 'confirmada' || newStatus === 'enviada' || newStatus === 'concluida') {
      const order = orders.find(o => o.id === orderId);
      const client = contacts.find(c => c.id === order?.client_id);
      setPendingStatusChange({
        orderId,
        newStatus,
        orderNumber: order?.order_number,
        clientName: order?.client_name,
        clientEmail: client?.email
      });
      setNotifyClient(!!client?.email); // Default to true if client has email
      setShowStatusConfirm(true);
      return;
    }
    
    // For other statuses, change directly
    try {
      await api().patch(`/orders/${orderId}/status`, { status: newStatus });
      toast.success('Status atualizado!');
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    }
  };

  const confirmStatusChange = async () => {
    if (!pendingStatusChange) return;
    
    setUpdatingStatus(true);
    try {
      const response = await api().patch(`/orders/${pendingStatusChange.orderId}/status`, { 
        status: pendingStatusChange.newStatus,
        notify_client: notifyClient
      });
      
      // Build success message
      let message = `Status atualizado para "${STATUS_CONFIG[pendingStatusChange.newStatus]?.label}"!`;
      if (response.data.email_notification) {
        message += ' Email enviado para a fábrica.';
      }
      if (response.data.client_notified) {
        message += ` Email enviado para o cliente.`;
      }
      if (response.data.client_email_warning) {
        toast.warning(response.data.client_email_warning);
      }
      
      toast.success(message);
      loadData();
    } catch (error) {
      toast.error('Erro ao atualizar status');
    } finally {
      setUpdatingStatus(false);
      setShowStatusConfirm(false);
      setPendingStatusChange(null);
    }
  };

  const resetForm = () => {
    setForm({
      client_id: '',
      client_name: '',
      items: [],
      subtotal: 0,
      discount: 0,
      total: 0,
      status: 'rascunho',
      notes: '',
      delivery_date: ''
    });
    setNewItem({
      product_id: '',
      product_name: '',
      quantity: 1,
      unit_price: 0
    });
  };

  const downloadOrderPDF = async (orderId, orderNumber) => {
    try {
      const response = await api().get(`/orders/${orderId}/pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `NE_${orderNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF descarregado!');
    } catch (error) {
      toast.error('Erro ao descarregar PDF');
    }
  };

  const downloadFactoryPDF = async (orderId, orderNumber) => {
    try {
      const response = await api().get(`/orders/${orderId}/factory-pdf`, {
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `FABRICA_${orderNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('PDF para Fábrica descarregado!');
    } catch (error) {
      toast.error('Erro ao descarregar PDF para Fábrica');
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('pt-PT', {
      style: 'currency',
      currency: 'EUR'
    }).format(value || 0);
  };

  const filteredOrders = orders.filter(o => 
    o.order_number?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    o.client_name?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="orders-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="relative flex-1 max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Pesquisar encomendas..."
            className="pl-9"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            data-testid="orders-search"
          />
        </div>
        <Button 
          className="bg-amber-500 hover:bg-amber-600 text-slate-900"
          onClick={() => { resetForm(); setShowModal(true); }}
          data-testid="new-order-btn"
        >
          <Plus className="h-4 w-4 mr-2" />
          Nova Nota de Encomenda
        </Button>
      </div>

      {/* Orders Table */}
      <Card>
        <CardContent className="p-0">
          {filteredOrders.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="h-16 w-16 mx-auto text-slate-300 mb-4" />
              <h3 className="text-lg font-semibold font-[Manrope]">Nenhuma encomenda encontrada</h3>
              <p className="text-muted-foreground mt-1">Crie a primeira nota de encomenda</p>
              <Button 
                className="mt-4 bg-amber-500 hover:bg-amber-600 text-slate-900"
                onClick={() => { resetForm(); setShowModal(true); }}
              >
                <Plus className="h-4 w-4 mr-2" />
                Criar Encomenda
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Nº ENCOMENDA</TableHead>
                  <TableHead>CLIENTE</TableHead>
                  <TableHead>DATA</TableHead>
                  <TableHead>TOTAL</TableHead>
                  <TableHead>ESTADO</TableHead>
                  <TableHead className="w-[150px]">AÇÕES</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOrders.map((order) => (
                  <TableRow key={order.id} data-testid={`order-row-${order.id}`}>
                    <TableCell className="font-mono font-medium">{order.order_number}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <User className="h-4 w-4 text-muted-foreground" />
                        {order.client_name}
                      </div>
                    </TableCell>
                    <TableCell>
                      {format(new Date(order.created_at), "d MMM yyyy", { locale: pt })}
                    </TableCell>
                    <TableCell className="font-medium tabular-nums">{formatCurrency(order.total)}</TableCell>
                    <TableCell>
                      <Select 
                        value={order.status} 
                        onValueChange={(v) => handleStatusChange(order.id, v)}
                      >
                        <SelectTrigger className="w-[130px] h-8">
                          <Badge className={STATUS_CONFIG[order.status]?.color || ''}>
                            {STATUS_CONFIG[order.status]?.label || order.status}
                          </Badge>
                        </SelectTrigger>
                        <SelectContent>
                          {Object.entries(STATUS_CONFIG).map(([key, config]) => (
                            <SelectItem key={key} value={key}>
                              {config.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleViewOrder(order)}
                          data-testid={`view-order-${order.id}`}
                        >
                          <Eye className="h-4 w-4 mr-1" />
                          Ver
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => downloadOrderPDF(order.id, order.order_number)}
                          title="Descarregar PDF"
                          data-testid={`download-pdf-${order.id}`}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="outline" 
                          size="sm"
                          className="text-amber-600 border-amber-600 hover:bg-amber-50"
                          onClick={() => downloadFactoryPDF(order.id, order.order_number)}
                          title="PDF para Fábrica"
                          data-testid={`factory-pdf-${order.id}`}
                        >
                          <Factory className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Create Order Modal */}
      <Dialog open={showModal} onOpenChange={(open) => { if (!open) resetForm(); setShowModal(open); }}>
        <DialogContent className="sm:max-w-[700px] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">Nova Nota de Encomenda</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit}>
            <div className="grid gap-4 py-4">
              {/* Client Selection */}
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Cliente *</Label>
                  <Select value={form.client_id} onValueChange={handleClientSelect}>
                    <SelectTrigger data-testid="order-client-select">
                      <SelectValue placeholder="Selecionar cliente" />
                    </SelectTrigger>
                    <SelectContent>
                      {contacts.map((contact) => (
                        <SelectItem key={contact.id} value={contact.id}>
                          {contact.name} {contact.company && `(${contact.company})`}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="delivery_date">Data de Entrega</Label>
                  <Input
                    id="delivery_date"
                    type="date"
                    value={form.delivery_date}
                    onChange={(e) => setForm({ ...form, delivery_date: e.target.value })}
                    data-testid="order-delivery-date"
                  />
                </div>
              </div>

              {/* Add Product */}
              <Card className="bg-slate-50">
                <CardHeader className="pb-2">
                  <CardTitle className="text-sm">Adicionar Produto</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-2 items-end">
                    <div className="flex-1 space-y-1">
                      <Label className="text-xs">Produto</Label>
                      <Select value={newItem.product_id} onValueChange={handleProductSelect}>
                        <SelectTrigger data-testid="order-product-select">
                          <SelectValue placeholder="Selecionar produto" />
                        </SelectTrigger>
                        <SelectContent>
                          {products.map((product) => (
                            <SelectItem key={product.id} value={product.id}>
                              {product.name} - {formatCurrency(product.price)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="w-24 space-y-1">
                      <Label className="text-xs">Qtd</Label>
                      <Input
                        type="number"
                        min="1"
                        value={newItem.quantity}
                        onChange={(e) => setNewItem({ ...newItem, quantity: parseFloat(e.target.value) || 1 })}
                        data-testid="order-quantity-input"
                      />
                    </div>
                    <div className="w-28 space-y-1">
                      <Label className="text-xs">Preço Unit.</Label>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={newItem.unit_price}
                        onChange={(e) => setNewItem({ ...newItem, unit_price: parseFloat(e.target.value) || 0 })}
                        data-testid="order-price-input"
                      />
                    </div>
                    <Button type="button" onClick={addItem} className="bg-slate-900 hover:bg-slate-800">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </CardContent>
              </Card>

              {/* Items List */}
              {form.items.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Produto</TableHead>
                        <TableHead className="text-right">Qtd</TableHead>
                        <TableHead className="text-right">Preço Unit.</TableHead>
                        <TableHead className="text-right">Total</TableHead>
                        <TableHead className="w-[50px]"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {form.items.map((item, index) => (
                        <TableRow key={index}>
                          <TableCell>{item.product_name}</TableCell>
                          <TableCell className="text-right tabular-nums">{item.quantity}</TableCell>
                          <TableCell className="text-right tabular-nums">{formatCurrency(item.unit_price)}</TableCell>
                          <TableCell className="text-right tabular-nums font-medium">{formatCurrency(item.total)}</TableCell>
                          <TableCell>
                            <Button variant="ghost" size="icon" className="text-red-500" onClick={() => removeItem(index)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </div>
              )}

              {/* Totals */}
              <div className="flex justify-end">
                <div className="w-64 space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>Subtotal:</span>
                    <span className="tabular-nums">{formatCurrency(form.subtotal)}</span>
                  </div>
                  <div className="flex justify-between text-sm items-center gap-2">
                    <span>Desconto:</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      className="w-24 h-8 text-right"
                      value={form.discount}
                      onChange={(e) => {
                        const discount = parseFloat(e.target.value) || 0;
                        setForm({ ...form, discount, total: form.subtotal - discount });
                      }}
                    />
                  </div>
                  <div className="flex justify-between font-bold text-lg border-t pt-2">
                    <span>Total:</span>
                    <span className="tabular-nums text-amber-600">{formatCurrency(form.total)}</span>
                  </div>
                </div>
              </div>

              {/* Notes */}
              <div className="space-y-2">
                <Label htmlFor="notes">Notas</Label>
                <Textarea
                  id="notes"
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  placeholder="Observações sobre a encomenda..."
                  data-testid="order-notes-input"
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowModal(false)}>
                Cancelar
              </Button>
              <Button type="submit" className="bg-amber-500 hover:bg-amber-600 text-slate-900" data-testid="save-order-btn">
                Criar Encomenda
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* View Order Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="sm:max-w-[600px]">
          <DialogHeader>
            <DialogTitle className="font-[Manrope]">
              Nota de Encomenda {viewingOrder?.order_number}
            </DialogTitle>
          </DialogHeader>
          {viewingOrder && (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <span className="text-muted-foreground">Cliente:</span>
                  <p className="font-medium">{viewingOrder.client_name}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Data:</span>
                  <p className="font-medium">{format(new Date(viewingOrder.created_at), "d 'de' MMMM 'de' yyyy", { locale: pt })}</p>
                </div>
                <div>
                  <span className="text-muted-foreground">Estado:</span>
                  <Badge className={`mt-1 ${STATUS_CONFIG[viewingOrder.status]?.color}`}>
                    {STATUS_CONFIG[viewingOrder.status]?.label}
                  </Badge>
                </div>
                {viewingOrder.delivery_date && (
                  <div>
                    <span className="text-muted-foreground">Entrega:</span>
                    <p className="font-medium">{format(new Date(viewingOrder.delivery_date), "d 'de' MMMM", { locale: pt })}</p>
                  </div>
                )}
              </div>

              <div className="border rounded-lg overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Produto</TableHead>
                      <TableHead className="text-right">Qtd</TableHead>
                      <TableHead className="text-right">Preço</TableHead>
                      <TableHead className="text-right">Total</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {viewingOrder.items?.map((item, index) => (
                      <TableRow key={index}>
                        <TableCell>{item.product_name}</TableCell>
                        <TableCell className="text-right tabular-nums">{item.quantity}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatCurrency(item.unit_price)}</TableCell>
                        <TableCell className="text-right tabular-nums">{formatCurrency(item.total)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              <div className="flex justify-end">
                <div className="w-48 space-y-1 text-sm">
                  <div className="flex justify-between">
                    <span>Subtotal:</span>
                    <span className="tabular-nums">{formatCurrency(viewingOrder.subtotal)}</span>
                  </div>
                  {viewingOrder.discount > 0 && (
                    <div className="flex justify-between text-red-600">
                      <span>Desconto:</span>
                      <span className="tabular-nums">-{formatCurrency(viewingOrder.discount)}</span>
                    </div>
                  )}
                  <div className="flex justify-between font-bold text-lg border-t pt-1">
                    <span>Total:</span>
                    <span className="tabular-nums">{formatCurrency(viewingOrder.total)}</span>
                  </div>
                </div>
              </div>

              {viewingOrder.notes && (
                <div className="p-3 bg-slate-50 rounded-lg">
                  <span className="text-xs text-muted-foreground">Notas:</span>
                  <p className="text-sm">{viewingOrder.notes}</p>
                </div>
              )}

              {/* WhatsApp Actions */}
              <div className="flex flex-wrap gap-2 pt-4 border-t">
                <Button
                  variant="outline"
                  size="sm"
                  className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                  onClick={() => handleResendEmail(viewingOrder.id)}
                  disabled={sendingEmail}
                >
                  {sendingEmail ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Mail className="h-4 w-4 mr-2" />
                  )}
                  {sendingEmail ? 'Enviando...' : 'Enviar p/ Fábrica'}
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                  onClick={() => {
                    const client = contacts.find(c => c.id === viewingOrder.client_id);
                    if (client?.phone) {
                      openWhatsApp(
                        client.phone, 
                        WhatsAppTemplates.orderConfirmation(
                          viewingOrder.client_name,
                          viewingOrder.order_number,
                          viewingOrder.total?.toFixed(2)
                        )
                      );
                    } else {
                      toast.error('Cliente não tem número de telefone');
                    }
                  }}
                >
                  <WhatsAppIcon className="h-4 w-4 mr-2" />
                  Enviar Confirmação
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                  onClick={() => {
                    const client = contacts.find(c => c.id === viewingOrder.client_id);
                    if (client?.phone) {
                      openWhatsApp(
                        client.phone, 
                        WhatsAppTemplates.orderReady(
                          viewingOrder.client_name,
                          viewingOrder.order_number
                        )
                      );
                    } else {
                      toast.error('Cliente não tem número de telefone');
                    }
                  }}
                >
                  <WhatsAppIcon className="h-4 w-4 mr-2" />
                  Pronta p/ Entrega
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  className="text-slate-600"
                  onClick={() => setShowHistory(!showHistory)}
                >
                  <History className="h-4 w-4 mr-2" />
                  {showHistory ? 'Ocultar Histórico' : 'Ver Histórico'}
                </Button>
              </div>

              {/* Email History Section */}
              {showHistory && (
                <div className="mt-4 pt-4 border-t">
                  <h4 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2">
                    <History className="h-4 w-4" />
                    Histórico de Emails Enviados
                  </h4>
                  
                  {loadingHistory ? (
                    <div className="flex items-center justify-center py-4">
                      <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
                      <span className="ml-2 text-sm text-slate-500">A carregar...</span>
                    </div>
                  ) : emailHistory.length === 0 ? (
                    <div className="text-center py-4 text-sm text-slate-500">
                      <Mail className="h-8 w-8 mx-auto mb-2 text-slate-300" />
                      <p>Nenhum email enviado ainda</p>
                    </div>
                  ) : (
                    <div className="space-y-2 max-h-48 overflow-y-auto">
                      {emailHistory.map((log) => (
                        <div 
                          key={log.id} 
                          className={`p-3 rounded-lg border ${
                            log.status === 'sent' 
                              ? 'bg-emerald-50 border-emerald-200' 
                              : 'bg-red-50 border-red-200'
                          }`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-center gap-2">
                              {log.status === 'sent' ? (
                                <CheckCircle2 className="h-4 w-4 text-emerald-600" />
                              ) : (
                                <XCircle className="h-4 w-4 text-red-600" />
                              )}
                              <span className={`text-sm font-medium ${
                                log.status === 'sent' ? 'text-emerald-700' : 'text-red-700'
                              }`}>
                                {log.status === 'sent' ? 'Enviado' : 'Falhou'}
                              </span>
                            </div>
                            <div className="flex items-center gap-1 text-xs text-slate-500">
                              <Clock className="h-3 w-3" />
                              {format(new Date(log.created_at), "d MMM yyyy, HH:mm", { locale: pt })}
                            </div>
                          </div>
                          <div className="mt-1 text-xs text-slate-600">
                            <p><strong>Para:</strong> {log.recipient}</p>
                            <p><strong>Por:</strong> {log.sent_by_name || 'Sistema'}</p>
                            {log.error_message && (
                              <p className="text-red-600 mt-1"><strong>Erro:</strong> {log.error_message}</p>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Status Change Confirmation Dialog */}
      <Dialog open={showStatusConfirm} onOpenChange={setShowStatusConfirm}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {pendingStatusChange?.newStatus === 'confirmada' ? (
                <>
                  <CheckCircle2 className="h-5 w-5 text-blue-600" />
                  Marcar como Confirmada
                </>
              ) : pendingStatusChange?.newStatus === 'enviada' ? (
                <>
                  <Send className="h-5 w-5 text-purple-600" />
                  Marcar como Enviada
                </>
              ) : (
                <>
                  <CheckCircle2 className="h-5 w-5 text-emerald-600" />
                  Marcar como Concluída
                </>
              )}
            </DialogTitle>
            <DialogDescription>
              Encomenda <strong>{pendingStatusChange?.orderNumber}</strong> - {pendingStatusChange?.clientName}
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Factory notification info */}
            <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-lg border border-blue-200">
              <Factory className="h-5 w-5 text-blue-600 flex-shrink-0" />
              <div className="text-sm">
                <p className="font-medium text-blue-900">Email para a Fábrica</p>
                <p className="text-blue-700">Será enviado automaticamente</p>
              </div>
            </div>

            {/* Client notification option */}
            <div className={`flex items-start gap-3 p-3 rounded-lg border ${
              pendingStatusChange?.clientEmail 
                ? 'bg-emerald-50 border-emerald-200' 
                : 'bg-amber-50 border-amber-200'
            }`}>
              <Checkbox
                id="notify-client"
                checked={notifyClient}
                onCheckedChange={setNotifyClient}
                disabled={!pendingStatusChange?.clientEmail}
                className="mt-0.5"
              />
              <div className="flex-1">
                <label 
                  htmlFor="notify-client" 
                  className={`text-sm font-medium cursor-pointer ${
                    pendingStatusChange?.clientEmail ? 'text-emerald-900' : 'text-amber-900'
                  }`}
                >
                  <span className="flex items-center gap-2">
                    <UserCheck className="h-4 w-4" />
                    Notificar o Cliente
                  </span>
                </label>
                {pendingStatusChange?.clientEmail ? (
                  <p className="text-sm text-emerald-700 mt-1">
                    Enviar para: <strong>{pendingStatusChange.clientEmail}</strong>
                  </p>
                ) : (
                  <p className="text-sm text-amber-700 mt-1 flex items-center gap-1">
                    <AlertCircle className="h-3 w-3" />
                    Cliente não tem email registado
                  </p>
                )}
              </div>
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button 
              variant="outline" 
              onClick={() => setShowStatusConfirm(false)}
              disabled={updatingStatus}
            >
              Cancelar
            </Button>
            <Button 
              onClick={confirmStatusChange}
              disabled={updatingStatus}
              className={
                pendingStatusChange?.newStatus === 'confirmada' 
                  ? 'bg-blue-600 hover:bg-blue-700' 
                  : pendingStatusChange?.newStatus === 'enviada' 
                    ? 'bg-purple-600 hover:bg-purple-700' 
                    : 'bg-emerald-600 hover:bg-emerald-700'
              }
            >
              {updatingStatus ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  A atualizar...
                </>
              ) : (
                <>
                  <Mail className="h-4 w-4 mr-2" />
                  Confirmar e Enviar
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
